import api from './api'

/**
 * Log levels
 */
export const LogLevel = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info'
}

/**
 * Rate limiting for error logging to prevent overwhelming the backend
 */
const errorLogRateLimiter = {
  maxErrors: 10, // Max errors per window
  windowMs: 60000, // 1 minute window
  errors: [],
  
  canLog() {
    const now = Date.now()
    // Remove errors outside the window
    this.errors = this.errors.filter(time => now - time < this.windowMs)
    
    if (this.errors.length >= this.maxErrors) {
      return false
    }
    
    this.errors.push(now)
    return true
  },
  
  getRemainingCount() {
    const now = Date.now()
    this.errors = this.errors.filter(time => now - time < this.windowMs)
    return this.maxErrors - this.errors.length
  }
}

/**
 * Log an error to the backend
 */
export const logError = async (error, context = {}) => {
  try {
    // Rate limit error logging
    if (!errorLogRateLimiter.canLog()) {
      if (import.meta.env.DEV) {
        console.warn('Error logging rate limit exceeded. Skipping backend log.')
      }
      return
    }
    
    const errorData = {
      message: error.message || String(error),
      stack: error.stack || '',
      url: window.location.href,
      userAgent: navigator.userAgent,
      level: LogLevel.ERROR,
      context: {
        ...context,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    }

    // Send to backend (don't await to avoid blocking)
    api.post('/log', errorData).catch(err => {
      // Silently fail - don't want logging errors to break the app
      // Don't log 429 errors as they indicate rate limiting
      if (err.response?.status !== 429 && import.meta.env.DEV) {
        console.error('Failed to log error to backend:', err)
      }
    })

    // Also log to console in development
    if (import.meta.env.DEV) {
      console.error('Error logged:', errorData)
    }
  } catch (err) {
    // Silently fail
    console.error('Error in logError:', err)
  }
}

/**
 * Log a warning to the backend
 */
export const logWarning = async (message, context = {}) => {
  try {
    // Rate limit warnings too
    if (!errorLogRateLimiter.canLog()) {
      if (import.meta.env.DEV) {
        console.warn('Warning logging rate limit exceeded. Skipping backend log.')
      }
      return
    }
    
    const warnData = {
      message,
      stack: '',
      url: window.location.href,
      userAgent: navigator.userAgent,
      level: LogLevel.WARN,
      context: {
        ...context,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    }

    api.post('/log', warnData).catch(err => {
      if (err.response?.status !== 429 && import.meta.env.DEV) {
        console.error('Failed to log warning to backend:', err)
      }
    })

    if (import.meta.env.DEV) {
      console.warn('Warning logged:', warnData)
    }
  } catch (err) {
    console.error('Error in logWarning:', err)
  }
}

/**
 * Log info to the backend
 */
export const logInfo = async (message, context = {}) => {
  try {
    const infoData = {
      message,
      stack: '',
      url: window.location.href,
      userAgent: navigator.userAgent,
      level: LogLevel.INFO,
      context: {
        ...context,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    }

    api.post('/log', infoData).catch(err => {
      console.error('Failed to log info to backend:', err)
    })

    if (import.meta.env.DEV) {
      console.info('Info logged:', infoData)
    }
  } catch (err) {
    console.error('Error in logInfo:', err)
  }
}

/**
 * Setup global error handlers
 */
export const setupErrorHandlers = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logError(event.reason, {
      type: 'unhandledRejection',
      promise: 'Promise rejection'
    })
  })

  // Handle global errors
  window.addEventListener('error', (event) => {
    logError(event.error || new Error(event.message), {
      type: 'globalError',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    })
  })
}

export default {
  logError,
  logWarning,
  logInfo,
  setupErrorHandlers,
  LogLevel
}
